import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/auth";
import { logError } from "@/lib/log";

const BG_URL =
  "https://images.unsplash.com/photo-1554684726-0830be8cdbcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHdoaXRlJTIwYXJjaGl0ZWN0dXJlJTIwbWluaW1hbHxlbnwwfHx8fDE3Nzg1MzU0OTR8MA&ixlib=rb-4.1.0&q=85";

/**
 * Recuperación de contraseña para instructor o gestor.
 * El rol se detecta por la URL (/instructor/... vs /admin/...).
 * El usuario introduce su email. El backend genera una contraseña
 * temporal, la envía por email y activa `must_change_password`.
 */
export default function RecuperarContrasenaScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin/");
  const roleLabel = isAdmin ? "gestor" : "instructor";
  const recoveryEndpoint = isAdmin
    ? "/admin/password-recovery"
    : "/instructor/password-recovery";
  const backTo = isAdmin ? "/admin/login" : "/instructor/login";

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Introduce tu correo electrónico.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}${recoveryEndpoint}`, {
        email: email.trim(),
      });
      setDone(true);
    } catch (err) {
      logError("Error en recuperación de contraseña", err);
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "No pudimos procesar la petición. Inténtalo de nuevo."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-center px-6 py-12"
      data-testid="recovery-screen"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_URL})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-white/85 backdrop-blur-xl" aria-hidden />
      <div className="relative z-10 w-full max-w-md">
        <button
          onClick={() => navigate(backTo)}
          className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors mb-6"
          data-testid="recovery-back-button"
        >
          <ChevronLeft className="h-4 w-4" />
          Volver a iniciar sesión
        </button>
        <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_1px_0_rgba(0,0,0,0.04),0_24px_60px_-24px_rgba(0,0,0,0.18)] p-8 md:p-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 mb-8">
            <span className="inline-block h-2 w-2 bg-black" />
            Recuperar contraseña
          </div>
          {done ? (
            <div className="space-y-4" data-testid="recovery-done">
              <ShieldCheck className="h-10 w-10 text-emerald-600" />
              <h1 className="heading-font text-3xl font-extrabold text-zinc-950">
                Correo enviado
              </h1>
              <p className="text-zinc-700">
                Si ese correo pertenece a un {roleLabel} registrado, en unos
                segundos recibirás un email con tu contraseña temporal.
              </p>
              <p className="text-sm text-zinc-500">
                La primera vez que entres con la nueva contraseña, la
                aplicación te pedirá que la cambies por una definitiva.
              </p>
              <Link
                to={backTo}
                className="inline-block mt-4 w-full text-center h-14 leading-[3.5rem] text-base font-semibold bg-zinc-950 hover:bg-zinc-800 text-white rounded-md transition-colors"
                data-testid="recovery-login-link"
              >
                Ir a iniciar sesión
              </Link>
            </div>
          ) : (
            <>
              <h1 className="heading-font text-4xl sm:text-5xl font-extrabold text-zinc-950 leading-[0.95]">
                ¿Olvidaste tu contraseña?
              </h1>
              <p className="mt-3 text-zinc-600 text-base">
                Introduce el correo electrónico que tienes registrado como
                {" "}{roleLabel}. Te enviaremos una contraseña temporal por
                email y podrás elegir una nueva al entrar.
              </p>
              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">
                    Correo electrónico
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                    <Input
                      type="email"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="usuario@dominio.com"
                      className="touch-input pl-12 border-2 border-zinc-300 focus-visible:border-zinc-900 focus-visible:ring-0"
                      data-testid="recovery-email-input"
                      autoFocus
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 text-base font-semibold bg-zinc-950 hover:bg-zinc-800 text-white rounded-md transition-colors mt-4"
                  data-testid="recovery-submit-button"
                >
                  {loading ? "Enviando…" : "Enviar correo de recuperación"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
